<template>
  <div
    @click="$emit('click', practice)"
    :class="[
      'relative px-4 py-3 cursor-pointer transition-all duration-200',
      selected
        ? 'bg-primary-50/50 border-l-[3px] border-l-primary-500'
        : 'border-l-[3px] border-l-transparent hover:bg-gray-50'
    ]"
  >
    <!-- Top row: Name + Actions + Bene badge -->
    <div class="flex justify-between items-start gap-2">
      <div class="sensitive font-semibold text-[13px] text-gray-900 truncate flex-1">
        {{ practice.NAME }}
      </div>
      <div class="flex items-center gap-1.5 shrink-0">
        <slot name="actions"></slot>
        <span
          v-if="practice.ATTRIBUTED_BENES"
          class="text-[11px] font-mono font-semibold px-2 py-0.5 rounded-md bg-gray-100 text-gray-700"
        >
          {{ formatNumber(practice.ATTRIBUTED_BENES) }}
        </span>
      </div>
    </div>

    <!-- IDs + Location + Providers row -->
    <div class="text-[11px] text-gray-500 mt-0.5 flex justify-between">
      <span>
        <span v-if="practice.CITY || practice.STATE">{{ practice.CITY ? practice.CITY + ', ' : '' }}{{ practice.STATE }}</span>
        <span v-if="(practice.CITY || practice.STATE) && practice.PROVIDER_COUNT" class="mx-1">·</span>
        <span v-if="practice.PROVIDER_COUNT">{{ practice.PROVIDER_COUNT }} providers</span>
      </span>
      <span>
        <span v-if="practice.PAC_ID">PAC: {{ practice.PAC_ID }}</span>
        <span v-if="practice.PAC_ID && practice.TIN_ID" class="mx-1">·</span>
        <span v-if="practice.TIN_ID">TIN: {{ practice.TIN_ID }}</span>
      </span>
    </div>

    <!-- Tags row -->
    <div v-if="showTags" class="mt-2 flex flex-wrap gap-1 items-center">
      <!-- ACO Status tags (slate) -->
      <PracticeTag
        v-if="practice.IN_MSSP"
        :title="practice.MSSP_ACO_NAME"
        category="aco"
        filled
      >MSSP</PracticeTag>
      <PracticeTag
        v-else-if="practice.IN_REACH"
        :title="practice.REACH_ACO_NAME"
        category="aco"
        filled
      >REACH</PracticeTag>
      <PracticeTag
        v-else-if="practice.PRIOR_MSSP"
        :title="'Left MSSP in ' + practice.PRIOR_MSSP_YEAR"
        category="volume"
      >Prior MSSP</PracticeTag>
      <PracticeTag
        v-else-if="practice.PRIOR_REACH"
        :title="'Left REACH in ' + practice.PRIOR_REACH_YEAR"
        category="population"
      >Prior REACH</PracticeTag>
      <!-- ACO Performance (single tag) -->
      <PracticeTag v-if="practice.ACO_SAVINGS_TIER" :variant="acoVariant" category="aco">{{ practice.ACO_SAVINGS_TIER }}</PracticeTag>

      <!-- Practice type tags (blue) -->
      <PracticeTag v-if="practice.PCP_HEAVY" category="type">PCP</PracticeTag>
      <PracticeTag
        v-if="practice.PRACTICE_SIZE === 'solo'"
        title="Single provider practice"
        category="type"
      >Solo</PracticeTag>
      <PracticeTag
        v-else-if="practice.PRACTICE_SIZE === 'large'"
        title="20+ providers"
        category="type"
      >Large</PracticeTag>

      <!-- Volume/trend tags (amber) -->
      <PracticeTag
        v-if="practice.VOLUME_TIER"
        category="volume"
      >{{ practice.VOLUME_TIER }}</PracticeTag>
      <PracticeTag
        v-if="practice.PAYMENT_TREND === 'Growing'"
        title="Growing >10% since 2021"
        category="volume"
        variant="success"
      >Growing</PracticeTag>
      <PracticeTag
        v-else-if="practice.PAYMENT_TREND === 'Declining'"
        title="Declining >10% since 2021"
        category="volume"
        variant="warning"
      >Declining</PracticeTag>

      <!-- Setting tags (teal) -->
      <PracticeTag
        v-if="practice.SETTING_PREFERENCE === 'Office-Based'"
        title=">80% office-based services"
        category="setting"
      >Office</PracticeTag>
      <PracticeTag
        v-if="practice.LOCATION_TYPE === 'Rural'"
        title="Rural practice"
        category="setting"
      >Rural</PracticeTag>
      <PracticeTag
        v-if="practice.NURSING_FACILITY_INVOLVEMENT && practice.NURSING_FACILITY_INVOLVEMENT !== 'no_nursing_facility'"
        title="Does SNF rounding"
        category="setting"
      >SNF</PracticeTag>
      <PracticeTag
        v-if="practice.INPATIENT_INVOLVEMENT === 'hospitalist'"
        title=">50% hospital-based revenue"
        category="setting"
      >Hospitalist</PracticeTag>
      <PracticeTag
        v-if="practice.HOME_VISIT_INVOLVEMENT && practice.HOME_VISIT_INVOLVEMENT !== 'no_home_visits'"
        title="Does home visits"
        category="setting"
      >Home</PracticeTag>

      <!-- Population tags (purple) -->
      <PracticeTag
        v-if="practice.PANEL_ACUITY === 'complex_acuity'"
        :title="'Avg HCC: ' + (practice.AVG_HCC || '2.0+')"
        category="population"
      >Complex</PracticeTag>
      <PracticeTag
        v-if="practice.DUAL_ELIGIBLE_LEVEL === 'dual_very_high'"
        category="population"
      >Safety Net</PracticeTag>
      <PracticeTag
        v-if="practice.SERVICE_MIX === 'Primary Care'"
        title=">60% E&M services"
        category="type"
      >Primary Care</PracticeTag>
      <PracticeTag
        v-else-if="practice.SERVICE_MIX === 'Procedural'"
        title="<30% E&M services"
        category="volume"
      >Procedural</PracticeTag>

      <!-- Tech tags (gray) -->
      <PracticeTag
        v-if="practice.EHR_VENDOR && visibleTagCount < maxVisibleTags"
        :title="practice.EHR_VENDOR"
        category="tech"
        class="max-w-[80px] truncate"
      >{{ practice.EHR_VENDOR }}</PracticeTag>

      <!-- Hospital affiliation -->
      <PracticeTag
        v-if="practice.HOSPITAL_AFFILIATED && visibleTagCount < maxVisibleTags"
        :title="practice.PRIMARY_HOSPITAL_NAME || 'Hospital affiliated'"
        category="volume"
        variant="warning"
      >Hospital</PracticeTag>

      <!-- Overflow indicator -->
      <span
        v-if="totalTagCount > maxVisibleTags"
        class="text-xs text-gray-500 px-1.5"
      >
        +{{ totalTagCount - maxVisibleTags }} more
      </span>
    </div>


  </div>
</template>

<script>
import PracticeTag from './PracticeTag.vue'

export default {
  name: 'PracticeListItem',
  components: {
    PracticeTag,
  },
  props: {
    practice: {
      type: Object,
      required: true,
    },
    selected: {
      type: Boolean,
      default: false,
    },
    showTags: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['click'],
  data() {
    return {
      maxVisibleTags: 6,
    }
  },
  computed: {
    visibleTagCount() {
      // Count visible tags for overflow calculation
      let count = 0
      const p = this.practice

      if (p.IN_MSSP || p.IN_REACH || p.PRIOR_MSSP || p.PRIOR_REACH) count++
      if (p.PCP_HEAVY) count++
      if (p.PRACTICE_SIZE === 'solo' || p.PRACTICE_SIZE === 'large') count++
      if (p.VOLUME_TIER) count++
      if (p.PAYMENT_TREND === 'Growing' || p.PAYMENT_TREND === 'Declining') count++
      if (p.SETTING_PREFERENCE === 'Office-Based') count++
      if (p.LOCATION_TYPE === 'Rural') count++
      if (p.NURSING_FACILITY_INVOLVEMENT && p.NURSING_FACILITY_INVOLVEMENT !== 'no_nursing_facility') count++
      if (p.INPATIENT_INVOLVEMENT === 'hospitalist') count++
      if (p.HOME_VISIT_INVOLVEMENT && p.HOME_VISIT_INVOLVEMENT !== 'no_home_visits') count++
      if (p.PANEL_ACUITY === 'complex_acuity') count++
      if (p.DUAL_ELIGIBLE_LEVEL === 'dual_very_high') count++
      if (p.SERVICE_MIX === 'Primary Care' || p.SERVICE_MIX === 'Procedural') count++

      if (p.ACO_SAVINGS_TIER) count++
      return count
    },
    acoVariant() {
      const t = this.practice?.ACO_SAVINGS_TIER
      if (['Strong Saver', 'Moderate Saver'].includes(t)) return 'success'
      if (t === 'Losing') return 'warning'
      if (t === 'Volatile') return 'error'
      return null
    },
    totalTagCount() {
      // Total including EHR and hospital
      let count = this.visibleTagCount
      if (this.practice.EHR_VENDOR) count++
      if (this.practice.HOSPITAL_AFFILIATED) count++
      return count
    },
  },
  methods: {
    formatNumber(num) {
      if (num === null || num === undefined) return '—'
      if (num >= 1000) return (num / 1000).toFixed(1) + 'K'
      return num.toLocaleString()
    },
  },
}
</script>
